// components/dashboard/DossiersRetard.js - Version corrigée cohérente
import React, { useEffect, useState } from "react";
import { db } from "../../firebase/config";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { Link } from "react-router-dom";
import { Clock, AlertTriangle, Calendar, CheckCircle, RefreshCw } from "lucide-react";
import TimelineDelayService from "../../services/TimelineDelayService";
import NotificationScheduler from "../../services/NotificationScheduler";

export default function DossiersRetard() {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statistics, setStatistics] = useState({
    overdueCount: 0,
    todayNotifications: 0,
    lastCheck: null
  });

  // Charger les dossiers en retard
  const fetchDossiersRetard = async () => {
    try {
      console.log("🔍 DEBUG: Début fetchDossiersRetard...");
      
      // ✅ CORRECTION : Requête simple d'abord, sans orderBy pour éviter les erreurs d'index
      const q = query(
        collection(db, "dossiers"), 
        where("status", "==", "en retard")
      );
      
      console.log("🔍 DEBUG: Exécution de la requête...");
      const snapshot = await getDocs(q);
      console.log(`🔍 DEBUG: ${snapshot.size} dossiers trouvés avec status='en retard'`);
      const dossiersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log("🔍 DEBUG: Dossiers récupérés:", dossiersData.map(d => ({
        id: d.id,
        clientName: d.clientName,
        status: d.status,
        delayDetectedAt: d.delayDetectedAt?.toDate?.()?.toLocaleDateString('fr-FR') || 'pas de date'
      })));

      // ✅ CORRECTION : Trier manuellement par date de détection (le plus récent d'abord)
      dossiersData.sort((a, b) => {
        const dateA = a.delayDetectedAt?.toDate?.() || new Date(0);
        const dateB = b.delayDetectedAt?.toDate?.() || new Date(0);
        return dateB - dateA; // Tri décroissant
      });

      // Enrichir avec les détails des retards actuels
      const enrichedDossiers = [];
      for (const dossier of dossiersData) {
        console.log(`🔍 DEBUG: Enrichissement dossier ${dossier.id}...`);
        
        try {
          // ✅ CORRECTION : checkDossierStepDelays retourne boolean, pas array
          const hasDelays = await TimelineDelayService.checkDossierStepDelays(dossier);
          
          // ✅ CORRECTION : Adapter les données à la structure attendue
          const adaptedDossier = {
            ...dossier,
            currentDelays: [], // On utilisera calculateCurrentDelays plus tard
            // ✅ CORRECTION : Créer delayInfo à partir des données existantes
            delayInfo: {
              detectedAt: dossier.delayDetectedAt, // Adapté depuis le champ réel
              delayedSteps: dossier.delayedSteps || [], // Adapté depuis le champ réel
              lastCheckedAt: dossier.updatedAt
            }
          };
          
          enrichedDossiers.push(adaptedDossier);
          console.log(`✅ DEBUG: Dossier ${dossier.id} enrichi avec succès`);
          
        } catch (error) {
          console.error(`❌ DEBUG: Erreur enrichissement dossier ${dossier.id}:`, error);
          // ✅ CORRECTION : Ajouter le dossier même en cas d'erreur d'enrichissement
          const fallbackDossier = {
            ...dossier,
            currentDelays: [],
            delayInfo: {
              detectedAt: dossier.delayDetectedAt,
              delayedSteps: dossier.delayedSteps || [],
              lastCheckedAt: dossier.updatedAt
            }
          };
          
          enrichedDossiers.push(fallbackDossier);
          console.log(`⚠️ DEBUG: Dossier ${dossier.id} ajouté avec données de base`);
        }
      }

      console.log(`🎯 DEBUG: Total dossiers finaux: ${enrichedDossiers.length}`);
      setDossiers(enrichedDossiers);
      
    } catch (error) {
      console.error("❌ DEBUG: Erreur dans fetchDossiersRetard:", error);
      console.error("❌ DEBUG: Type d'erreur:", error.constructor.name);
      console.error("❌ DEBUG: Message:", error.message);
      console.error("❌ DEBUG: Stack:", error.stack);
      setDossiers([]);
    }
  };

  // Charger les statistiques
  const fetchStatistics = async () => {
    try {
      console.log("🔍 DEBUG: Début fetchStatistics...");
      
      // ✅ CORRECTION : Utiliser exactement la même requête que fetchDossiersRetard
      const statsQuery = query(
        collection(db, "dossiers"),
        where("status", "==", "en retard")
      );
      
      console.log("🔍 DEBUG: Exécution requête statistiques...");
      const statsSnapshot = await getDocs(statsQuery);
      const totalOverdue = statsSnapshot.size;
      
      console.log(`🔍 DEBUG: Statistiques - ${totalOverdue} dossiers en retard trouvés`);
      
      // Compter les notifications d'aujourd'hui (approximation)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let todayNotifications = 0;
      statsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.delayDetectedAt) {
          const detectedDate = data.delayDetectedAt.toDate();
          if (detectedDate >= today) {
            todayNotifications++;
          }
        }
      });
      
      console.log(`🔍 DEBUG: ${todayNotifications} notifications aujourd'hui`);
      
      setStatistics({
        overdueCount: totalOverdue,
        todayNotifications: todayNotifications,
        lastCheck: new Date() // Approximation
      });
      
      console.log("✅ DEBUG: Statistiques mises à jour avec succès");
      
    } catch (error) {
      console.error("❌ DEBUG: Erreur chargement statistiques:", error);
      setStatistics({
        overdueCount: 0,
        todayNotifications: 0,
        lastCheck: null
      });
    }
  };

  // Rafraîchir manuellement
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Forcer une vérification des délais
      await TimelineDelayService.checkAllDossierDelays();
      
      // Recharger les données
      await Promise.all([
        fetchDossiersRetard(),
        fetchStatistics()
      ]);
      
    } catch (error) {
      console.error("Erreur lors du rafraîchissement:", error);
    } finally {
      setRefreshing(false);
    }
  };

  // Chargement initial
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchDossiersRetard(),
          fetchStatistics()
        ]);
      } catch (error) {
        console.error("Erreur chargement initial:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Formater la date
  const formatDate = (timestamp) => {
    if (!timestamp) return "Non définie";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Calculer la sévérité du retard
  const getDelayColor = (daysOverdue) => {
    if (daysOverdue <= 3) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    if (daysOverdue <= 7) return "text-orange-600 bg-orange-50 border-orange-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const getDelayIcon = (daysOverdue) => {
    if (daysOverdue <= 3) return <Clock className="w-4 h-4" />;
    if (daysOverdue <= 7) return <AlertTriangle className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  // ✅ CORRECTION : Fonction pour calculer les retards à partir des étapes sauvegardées
  const calculateCurrentDelays = (dossier) => {
    if (!dossier.delayedSteps || !Array.isArray(dossier.delayedSteps)) {
      return [];
    }
    
    return dossier.delayedSteps.map(step => ({
      stepName: step.stepName,
      daysOverdue: step.delayDays,
      deadlineDate: step.deadline,
      category: step.category
    }));
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <AlertTriangle className="w-8 h-8 text-red-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Dossiers en Retard ({dossiers.length})
            </h1>
            <p className="text-sm text-gray-600">
              Suivi des dossiers avec étapes non validées dans les délais
            </p>
          </div>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <div>
              <p className="text-sm text-red-600 font-medium">Dossiers en retard</p>
              <p className="text-2xl font-bold text-red-700">{statistics.overdueCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-blue-600 mr-2" />
            <div>
              <p className="text-sm text-blue-600 font-medium">Notifications aujourd'hui</p>
              <p className="text-2xl font-bold text-blue-700">{statistics.todayNotifications}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <Calendar className="w-5 h-5 text-gray-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600 font-medium">Dernière vérification</p>
              <p className="text-sm font-bold text-gray-700">
                {statistics.lastCheck ? formatDate(statistics.lastCheck) : 'Jamais'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des dossiers */}
      {dossiers.length === 0 ? (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
            <div>
              <p className="text-green-700 font-medium">Aucun dossier en retard</p>
              <p className="text-green-600 text-sm">
                Toutes les étapes sont validées dans les délais impartis.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {dossiers.map(dossier => {
            // ✅ CORRECTION : Utiliser les données réelles sauvegardées par TimelineDelayService
            const currentDelays = calculateCurrentDelays(dossier);
            
            return (
              <div key={dossier.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="p-6">
                  {/* En-tête du dossier */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {dossier.clientName || `Dossier ${dossier.id}`}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Créé le {formatDate(dossier.createdAt)}
                      </p>
                      {/* ✅ CORRECTION : Utiliser le bon champ */}
                      {dossier.delayDetectedAt && (
                        <p className="text-sm text-red-600 mt-1">
                          Retard détecté le {formatDate(dossier.delayDetectedAt)}
                        </p>
                      )}
                    </div>
                    
                    <Link
                      to={`/admin/dossier/${dossier.id}`}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Voir le dossier
                    </Link>
                  </div>

                  {/* Étapes en retard actuelles */}
                  {currentDelays && currentDelays.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Étapes actuellement en retard :
                      </h4>
                      <div className="space-y-2">
                        {currentDelays.map((delay, index) => (
                          <div
                            key={index}
                            className={`flex items-center justify-between p-3 rounded-lg border ${getDelayColor(delay.daysOverdue)}`}
                          >
                            <div className="flex items-center">
                              {getDelayIcon(delay.daysOverdue)}
                              <span className="ml-2 font-medium">
                                {delay.stepName}
                              </span>
                            </div>
                            <div className="text-right">
                              <span className="font-bold">
                                {delay.daysOverdue} jour{delay.daysOverdue > 1 ? 's' : ''} de retard
                              </span>
                              {delay.deadlineDate && (
                                <p className="text-xs opacity-75">
                                  Échéance : {formatDate(delay.deadlineDate)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Informations historiques de retard */}
                  {/* ✅ CORRECTION : Utiliser les bonnes données */}
                  {dossier.delayedSteps && dossier.delayedSteps.length > 0 && (
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Historique des retards détectés :
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {dossier.delayedSteps.map((step, index) => (
                          <div key={index} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                            <span className="font-medium">{step.stepName}</span>
                            <span className="ml-2 text-red-600">
                              ({step.delayDays} jour{step.delayDays > 1 ? 's' : ''} de retard)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Informations supplémentaires */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mt-4 pt-4 border-t border-gray-100">
                    <span>ID: {dossier.id}</span>
                    <span>
                      {/* ✅ CORRECTION : Utiliser le bon champ */}
                      Dernière vérification: {formatDate(dossier.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}